import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Chip, IconButton, Stack, TextField, Typography } from '@mui/material';
import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { BasicDialog } from '../../../../components/Dialog/BasicDialog';
import { FieldSelect } from '../../../../components/Form/Display/FieldSelect';
import { StickyFormButtons } from '../../../../components/Form/StickyFormButtons';
import { MaxModalFormWidth, ModalFormWidth } from '../../../../constants/styles';
import { RendererType } from '../../../../data-models/field-definition';
import { ISelectMeta } from '../../../../data-models/field3.data-model';
import { PermissionKey } from '../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../services/PermissionService';
import {
  CaptableChangeType,
  ClassificationProvenance,
  ICaptableVersion,
  updateVersionClassification,
} from '../../../../services/queries/MaggieCapTableQueries';
import { getErrorMessage } from '../../../../services/queryHelpers';
import { useToastMessageState } from '../../../../components/ToastMessage/ToastMessageProvider';
import { createFormField } from '../../../../view-models/form.view-model';
import { captableChangeTypeOptions } from '../captable-utils';
import { useInvalidateCaptables } from '../useInvalidateCaptables';

/**
 * Classification flags for the selected version plus an inline edit affordance (FD-4108).
 * Renders nothing for an unclassified version unless the user can edit (they may still classify it).
 */
export function VersionClassificationInfo({
  version,
  companyId,
}: {
  version: ICaptableVersion | null;
  companyId: number;
}) {
  const [editing, setEditing] = useState(false);
  const canEdit = PermissionService.get().hasPermission(PermissionKey.canWriteCaptable);

  if (!version) {
    return null;
  }

  // Low-key provenance signal: the chip is gone, so the pencil's tooltip carries it.
  const isManual = version.classificationProvenance === ClassificationProvenance.Manual;
  const editTitle = isManual ? 'Edit change classification (manually edited)' : 'Edit change classification';

  return (
    <Stack direction={'row'} alignItems={'center'} gap={'0.5rem'}>
      {version.needsReview && (
        <Chip
          size={'small'}
          color={'warning'}
          variant={'outlined'}
          icon={<WarningAmberOutlinedIcon />}
          label={'Needs review'}
        />
      )}
      {version.restatedPeriod && (
        <Chip size={'small'} color={'info'} variant={'outlined'} label={'Restated'} />
      )}
      {canEdit && (
        <IconButton size={'small'} title={editTitle} onClick={() => setEditing(true)}>
          <EditOutlinedIcon fontSize={'small'} color={'secondary'} />
        </IconButton>
      )}
      {editing && (
        <EditClassificationDialog version={version} companyId={companyId} onClose={() => setEditing(false)} />
      )}
    </Stack>
  );
}

function DialogField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap={'0.25rem'}>
      <Typography
        variant={'caption'}
        color={'text.secondary'}
        sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
      >
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

const EditClassificationDialog: FC<{
  version: ICaptableVersion;
  companyId: number;
  onClose: () => void;
}> = ({ version, companyId, onClose }) => {
  const invalidateCaptables = useInvalidateCaptables();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const [changeType, setChangeType] = useState<CaptableChangeType | undefined>(version.changeType);
  const [label, setLabel] = useState(version.changeLabel ?? '');
  const [subtype, setSubtype] = useState(version.changeSubtype ?? '');
  const [loading, setLoading] = useState(false);

  const typeField = useMemo(
    () =>
      createFormField<ISelectMeta<CaptableChangeType>>({
        key: 'changeType',
        label: '',
        dataType: 'string',
        renderer: RendererType.singleSelect,
        rendererMeta: { values: captableChangeTypeOptions, multi: false },
        disableClearable: true,
        selectOnFocus: false,
      }),
    []
  );

  // BE rejects an empty body (422); require at least one field.
  const canSave = changeType != null || label.trim() !== '' || subtype.trim() !== '';

  const onSubmit = useCallback(async () => {
    setLoading(true);
    try {
      // The classification lives on the version's anchor snapshot when later snapshots were absorbed.
      await updateVersionClassification(version.classificationCaptableId ?? version.id, {
        changeType,
        label: label.trim() || undefined,
        subtype: subtype.trim() || undefined,
      });
      await invalidateCaptables(companyId, version);
      pushSuccessToast({ message: 'Classification updated' });
      onClose();
    } catch (err) {
      console.error(err);
      pushErrorToast({ message: getErrorMessage(err, 'Failed to update classification') });
    } finally {
      setLoading(false);
    }
  }, [
    changeType,
    label,
    subtype,
    version,
    companyId,
    invalidateCaptables,
    onClose,
    pushSuccessToast,
    pushErrorToast,
  ]);

  return (
    <BasicDialog
      titleComponent={<Typography>Edit Change Classification</Typography>}
      open={true}
      onClose={onClose}
    >
      <Stack
        style={{ padding: '0.25rem 2.5rem 0', width: ModalFormWidth, maxWidth: MaxModalFormWidth }}
        gap={'1rem'}
      >
        <DialogField label={'Change Type'}>
          <FieldSelect
            formField={typeField}
            formProps={
              { onChange: (val) => setChangeType(val), value: changeType ?? null } as ControllerRenderProps
            }
          />
        </DialogField>
        <DialogField label={'Label'}>
          <TextField
            value={label}
            placeholder={'e.g. Series B Close'}
            onChange={(e) => setLabel(e.target.value)}
            inputProps={{ maxLength: 120 }}
          />
        </DialogField>
        <DialogField label={'Subtype'}>
          <TextField
            value={subtype}
            placeholder={'e.g. SAFE'}
            onChange={(e) => setSubtype(e.target.value)}
            inputProps={{ maxLength: 120 }}
          />
        </DialogField>
      </Stack>
      <StickyFormButtons
        onSubmit={onSubmit}
        onCancel={onClose}
        submitLabel={'Save'}
        loading={loading}
        disabled={!canSave}
      />
    </BasicDialog>
  );
};
